import React from "react";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { SectionHeader } from "../components/section-header";
import { SectionLoader } from "../components/section-loader";
import { EmptyState } from "@/components/shared/empty-state";
import { FileText, Download, ArrowRight } from "lucide-react";

interface WhitepapersSectionProps {
  items: any[];
  isLoading: boolean;
}

export function WhitepapersSection({ items, isLoading }: WhitepapersSectionProps) {
  const displayItems = items.length > 0 ? items.slice(0, 4) : [];

  if (!displayItems.length && !isLoading) return null;

  return (
    <section className="border-b border-border" aria-labelledby="whitepapers-heading">
      <div className="mx-auto max-w-7xl px-5 py-16 sm:py-20">
        <SectionHeader
          eyebrow="Research"
          icon={FileText}
          title="Whitepapers"
          href="/whitepapers"
        />
        {isLoading ? (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-24 bg-neutral-100 rounded-xl animate-pulse" />
            ))}
          </div>
        ) : displayItems.length > 0 ? (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {displayItems.map((item: any) => (
              <Card key={item.id} className="group hover:border-neutral-300 transition-all duration-150">
                <CardContent className="p-5">
                  <div className="flex items-start gap-4">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary shrink-0">
                      <FileText className="h-5 w-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-heading font-semibold text-foreground group-hover:text-primary transition-colors line-clamp-2">
                        {item.title}
                      </h3>
                      <p className="mt-1 text-xs text-muted-foreground">{item.pageCount ?? 0} pages</p>
                      <Button variant="ghost" size="sm" asChild className="mt-3 p-0 h-auto text-xs gap-1">
                        <Link href={`/whitepapers`}>
                          <Download className="h-3 w-3" /> Download
                        </Link>
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <EmptyState icon={FileText} description="No whitepapers yet." />
        )}
      </div>
    </section>
  );
}